import { View, Text, styled, useTheme } from 'tamagui'
import { Search, X, Filter, Calendar, Hash, User } from '@tamagui/lucide-icons'
import { useState, useRef, useEffect, useMemo } from 'react'
import { useAppStore, useServerStore } from '@/store'
import { messages, users } from '@/mocks'
import { Avatar } from '../../common'

const Overlay = styled(View, {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  alignItems: 'center',
  paddingTop: 80,
  zIndex: 4000,
})

const Modal = styled(View, {
  width: 540,
  maxHeight: 500,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$borderDefault',
  flexDirection: 'column',
  overflow: 'hidden',
})

const SearchHeader = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  paddingHorizontal: 16,
  height: 52,
  gap: 10,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const FilterBar = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  paddingHorizontal: 12,
  paddingVertical: 8,
  gap: 6,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  flexWrap: 'wrap',
})

const FilterChip = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  paddingHorizontal: 8,
  paddingVertical: 4,
  borderRadius: '$borderRadiusSm',
  backgroundColor: '$sidebar',
  borderWidth: 1,
  borderColor: '$borderDefault',
  cursor: 'pointer',
  hoverStyle: { borderColor: '$accent' },
  variants: {
    active: {
      true: {
        borderColor: '$accent',
        backgroundColor: 'rgba(62, 207, 207, 0.1)',
      },
    },
  } as const,
})

const DropdownMenu = styled(View, {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 4,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusMd',
  borderWidth: 1,
  borderColor: '$borderDefault',
  paddingVertical: 4,
  zIndex: 10,
  minWidth: 180,
  maxHeight: 200,
  overflow: 'hidden',
})

const DropdownItem = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  paddingHorizontal: 10,
  paddingVertical: 6,
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const DateInputRow = styled(View, {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 4,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusMd',
  borderWidth: 1,
  borderColor: '$borderDefault',
  padding: 10,
  zIndex: 10,
  flexDirection: 'row',
  gap: 8,
  alignItems: 'center',
})

const ResultItem = styled(View, {
  flexDirection: 'row',
  gap: 10,
  paddingHorizontal: 16,
  paddingVertical: 10,
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const EmptyState = styled(View, {
  alignItems: 'center',
  justifyContent: 'center',
  padding: 48,
  gap: 12,
})

export function SearchModal() {
  const { toggleSearch, activeServerId } = useAppStore()
  const server = useServerStore((s) => activeServerId ? s.servers[activeServerId] : undefined)
  const theme = useTheme()
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Filter state
  const [filterUser, setFilterUser] = useState<string | null>(null)
  const [filterChannel, setFilterChannel] = useState<string | null>(null)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Dropdown visibility
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [showChannelDropdown, setShowChannelDropdown] = useState(false)
  const [showDateDropdown, setShowDateDropdown] = useState(false)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') toggleSearch()
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [toggleSearch])

  // Get all channels for active server
  const serverChannels = server?.channels.filter(c => c.type === 'text' || c.type === 'announcement') || []

  // Collect all messages across channels (or from filtered channel)
  const allMessages = useMemo(() => {
    if (filterChannel) {
      return messages[filterChannel] || []
    }
    // Search across all channels in the server
    const channelIds = serverChannels.map(c => c.id)
    const result: typeof messages[string] = []
    for (const chId of channelIds) {
      const msgs = messages[chId]
      if (msgs) result.push(...msgs)
    }
    return result
  }, [filterChannel, serverChannels])

  const results = useMemo(() => {
    if (!query.trim()) return []

    let filtered = allMessages.filter((m) =>
      m.content.toLowerCase().includes(query.toLowerCase())
    )

    if (filterUser) {
      filtered = filtered.filter(m => m.authorId === filterUser)
    }

    if (dateFrom) {
      const fromTs = new Date(dateFrom).getTime()
      filtered = filtered.filter(m => m.timestamp >= fromTs)
    }

    if (dateTo) {
      const toTs = new Date(dateTo).getTime() + 86_400_000 // end of day
      filtered = filtered.filter(m => m.timestamp <= toTs)
    }

    return filtered.sort((a, b) => b.timestamp - a.timestamp)
  }, [query, allMessages, filterUser, dateFrom, dateTo])

  const hasActiveFilters = filterUser || filterChannel || dateFrom || dateTo

  const closeAllDropdowns = () => {
    setShowUserDropdown(false)
    setShowChannelDropdown(false)
    setShowDateDropdown(false)
  }

  const selectedUserName = filterUser ? users[filterUser]?.displayName : null
  const selectedChannelName = filterChannel
    ? serverChannels.find(c => c.id === filterChannel)?.name
    : null

  return (
    <Overlay onPress={toggleSearch}>
      <Modal
        onPress={(e: any) => {
          e.stopPropagation()
          closeAllDropdowns()
        }}
        // @ts-expect-error -- web box-shadow
        style={{ boxShadow: theme.shadowModal?.val || 'none' }}
      >
        <SearchHeader>
          <Search size={18} color="$sidebarMuted" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search messages..."
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: 'inherit',
              fontFamily: '$fontBody',
              fontSize: 15,
              flex: 1,
            }}
          />
          <View cursor="pointer" onPress={toggleSearch}>
            <X size={18} color="$sidebarMuted" />
          </View>
        </SearchHeader>

        <FilterBar>
          <Filter size={12} color="$sidebarMuted" />
          <Text fontFamily="$fontBody" fontSize={11} color="$sidebarMuted">Filters:</Text>

          {/* From user chip */}
          <View position="relative">
            <FilterChip
              active={!!filterUser}
              onPress={(e: any) => {
                e.stopPropagation()
                closeAllDropdowns()
                setShowUserDropdown(!showUserDropdown)
              }}
            >
              <User size={12} color={filterUser ? '$accent' : '$sidebarMuted'} />
              <Text fontFamily="$fontBody" fontSize={12} color={filterUser ? '$accent' : '$sidebarMuted'}>
                {selectedUserName || 'From user'}
              </Text>
              {filterUser && (
                <View
                  onPress={(e: any) => {
                    e.stopPropagation()
                    setFilterUser(null)
                  }}
                  cursor="pointer"
                >
                  <X size={10} color="$accent" />
                </View>
              )}
            </FilterChip>
            {showUserDropdown && (
              <DropdownMenu onPress={(e: any) => e.stopPropagation()}>
                {Object.values(users).map((u) => (
                  <DropdownItem
                    key={u.id}
                    onPress={() => {
                      setFilterUser(u.id)
                      setShowUserDropdown(false)
                    }}
                  >
                    <Avatar name={u.displayName} color={u.roleColor || '#8B95A8'} size="xs" showStatus={false} />
                    <Text fontFamily="$fontBody" fontSize={12} color="$surfaceForeground">{u.displayName}</Text>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            )}
          </View>

          {/* In channel chip */}
          <View position="relative">
            <FilterChip
              active={!!filterChannel}
              onPress={(e: any) => {
                e.stopPropagation()
                closeAllDropdowns()
                setShowChannelDropdown(!showChannelDropdown)
              }}
            >
              <Hash size={12} color={filterChannel ? '$accent' : '$sidebarMuted'} />
              <Text fontFamily="$fontBody" fontSize={12} color={filterChannel ? '$accent' : '$sidebarMuted'}>
                {selectedChannelName ? `#${selectedChannelName}` : 'In channel'}
              </Text>
              {filterChannel && (
                <View
                  onPress={(e: any) => {
                    e.stopPropagation()
                    setFilterChannel(null)
                  }}
                  cursor="pointer"
                >
                  <X size={10} color="$accent" />
                </View>
              )}
            </FilterChip>
            {showChannelDropdown && (
              <DropdownMenu onPress={(e: any) => e.stopPropagation()}>
                {serverChannels.map((ch) => (
                  <DropdownItem
                    key={ch.id}
                    onPress={() => {
                      setFilterChannel(ch.id)
                      setShowChannelDropdown(false)
                    }}
                  >
                    <Hash size={12} color="$sidebarMuted" />
                    <Text fontFamily="$fontBody" fontSize={12} color="$surfaceForeground">{ch.name}</Text>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            )}
          </View>

          {/* Date range chip */}
          <View position="relative">
            <FilterChip
              active={!!(dateFrom || dateTo)}
              onPress={(e: any) => {
                e.stopPropagation()
                closeAllDropdowns()
                setShowDateDropdown(!showDateDropdown)
              }}
            >
              <Calendar size={12} color={(dateFrom || dateTo) ? '$accent' : '$sidebarMuted'} />
              <Text fontFamily="$fontBody" fontSize={12} color={(dateFrom || dateTo) ? '$accent' : '$sidebarMuted'}>
                {dateFrom || dateTo ? `${dateFrom || '...'} - ${dateTo || '...'}` : 'Date range'}
              </Text>
              {(dateFrom || dateTo) && (
                <View
                  onPress={(e: any) => {
                    e.stopPropagation()
                    setDateFrom('')
                    setDateTo('')
                  }}
                  cursor="pointer"
                >
                  <X size={10} color="$accent" />
                </View>
              )}
            </FilterChip>
            {showDateDropdown && (
              <DateInputRow onPress={(e: any) => e.stopPropagation()}>
                <View gap={2}>
                  <Text fontFamily="$fontBody" fontSize={10} color="$sidebarMuted">From</Text>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
                      borderRadius: 4,
                      padding: '4px 6px',
                      color: theme.surfaceForeground?.val || '#E2E8F0',
                      fontFamily: 'inherit',
                      fontSize: 12,
                      outline: 'none',
                    }}
                  />
                </View>
                <View gap={2}>
                  <Text fontFamily="$fontBody" fontSize={10} color="$sidebarMuted">To</Text>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
                      borderRadius: 4,
                      padding: '4px 6px',
                      color: theme.surfaceForeground?.val || '#E2E8F0',
                      fontFamily: 'inherit',
                      fontSize: 12,
                      outline: 'none',
                    }}
                  />
                </View>
              </DateInputRow>
            )}
          </View>
        </FilterBar>

        <View flex={1} maxHeight={380}>
          {query.trim() === '' ? (
            <EmptyState>
              <Search size={32} color="$color8" />
              <Text fontFamily="$fontBody" fontSize={14} color="$sidebarMuted">
                Search for messages across all channels
              </Text>
              <Text fontFamily="$fontBody" fontSize={12} color="$color8">
                Tip: Use filters to narrow results
              </Text>
            </EmptyState>
          ) : results.length === 0 ? (
            <EmptyState>
              <Text fontSize={32} opacity={0.3}>🔍</Text>
              <Text fontFamily="$fontBody" fontSize={14} color="$sidebarMuted">
                No results for "{query}"
              </Text>
            </EmptyState>
          ) : (
            <View>
              <View paddingHorizontal={16} paddingVertical={8} flexDirection="row" justifyContent="space-between" alignItems="center">
                <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">
                  {results.length} result{results.length !== 1 ? 's' : ''}
                  {hasActiveFilters ? ' (filtered)' : ''}
                </Text>
              </View>
              {results.slice(0, 20).map((msg) => {
                const user = users[msg.authorId]
                if (!user) return null
                const channel = serverChannels.find(c => c.id === msg.channelId)
                return (
                  <ResultItem key={msg.id}>
                    <Avatar name={user.displayName} color={user.roleColor || '#8B95A8'} size="sm" showStatus={false} />
                    <View flex={1} gap={2}>
                      <View flexDirection="row" alignItems="baseline" gap={6}>
                        <Text fontFamily="$fontBody" fontSize={13} fontWeight="600" color={user.roleColor || '$surfaceForeground'}>
                          {user.displayName}
                        </Text>
                        {channel && (
                          <Text fontFamily="$fontMono" fontSize={10} color="$accent">
                            #{channel.name}
                          </Text>
                        )}
                        <Text fontFamily="$fontMono" fontSize={10} color="$color8">
                          {new Date(msg.timestamp).toLocaleDateString()}
                        </Text>
                      </View>
                      <Text fontFamily="$fontMono" fontSize={13} color="$surfaceForeground" numberOfLines={2}>
                        {msg.content}
                      </Text>
                    </View>
                  </ResultItem>
                )
              })}
            </View>
          )}
        </View>
      </Modal>
    </Overlay>
  )
}
